/**
 * Chão (blocos assados), estáticos altos e minimapa a partir do mapa v2.
 * O laço só pergunta o que desenhar; não sabe o que é Britain.
 */

import { blocosVisiveis, desenharBlocos, Z_PX } from './blocos';
import type { MapaCarregado } from './mapa';

type Ctx2D = CanvasRenderingContext2D;

const FAMILIA_COR: Record<string, string> = {
  grama: '#3c5828',
  terra: '#6b5836',
  calcada: '#6f6c64',
  agua: '#22496a',
  areia: '#9d8b60',
  madeira: '#6b4a28',
  tabua: '#7a5a34',
  piso: '#8a7048'
};

export function wx(tx: number, ty: number, tw: number): number {
  return (tx - ty) * tw / 2;
}

export function wy(tx: number, ty: number, th: number): number {
  return (tx + ty) * th / 2;
}

export function desenharChao(
  ctx: Ctx2D,
  mapa: MapaCarregado,
  cam: { x: number; y: number },
  vw: number,
  vh: number,
  zoom: number
): void {
  desenharBlocos(ctx, mapa, cam, vw, vh, zoom);
}

export function brilharAgua(
  ctx: Ctx2D,
  mapa: MapaCarregado,
  cam: { x: number; y: number },
  vw: number,
  vh: number,
  zoom: number,
  time: number
): void {
  const vis = blocosVisiveis(mapa, cam, vw, vh, zoom);
  const TW = mapa.tileW, TH = mapa.tileH;
  ctx.save();
  ctx.fillStyle = 'rgba(180,220,245,.5)';
  for (const { bx, by } of vis) {
    const tiles = mapa.aguaNoBloco(bx, by);
    if (tiles.length === 0) continue;
    for (const { tx, ty, z, h } of tiles) {
      const ph = (time * 0.35 + h) % 1;
      const sx = (wx(tx, ty, TW) - cam.x) * zoom + vw / 2 + (h * TW - TW / 2) * 0.5 * zoom;
      const sy = (wy(tx, ty, TH) + TH / 2 - z * Z_PX - cam.y) * zoom + vh / 2
        + Math.sin(ph * Math.PI * 2) * 4 * zoom;
      if (sx < -40 || sx > vw + 40 || sy < -40 || sy > vh + 40) continue;
      ctx.globalAlpha = 0.16 + Math.sin(ph * Math.PI) * 0.32;
      ctx.fillRect(sx, sy, 5 * zoom, 1 * zoom);
    }
  }
  ctx.restore();
}

/**
 * O def que um estático mostra agora: o próprio, ou o quadro da animação da UO pelo relógio do jogo
 * (techspec-dia-e-noite). Todos os itens iguais piscam juntos, sem estado por item.
 */
export function quadroDoEstatico(mapa: MapaCarregado, tile: number, agora: number) {
  const t = mapa.estaticosTs.tile(tile);
  const q = t.quadros;
  if (!q || q.length < 2) return t;
  const passo = t.passoMs ?? 50;
  return mapa.estaticosTs.tile(q[Math.floor(agora / passo) % q.length]);
}

/** Estático animado: fica fora do forro assado e é desenhado todo quadro. */
export function ehAnimado(mapa: MapaCarregado, tile: number): boolean {
  return (mapa.estaticosTs.tile(tile).quadros?.length ?? 0) > 1;
}

export function desenharEstatico(
  ctx: Ctx2D,
  mapa: MapaCarregado,
  e: MapaCarregado['estaticos'][number],
  cam: { x: number; y: number },
  vw: number,
  vh: number,
  zoom: number,
  alpha: number
): void {
  const TW = mapa.tileW, TH = mapa.tileH;
  const t = quadroDoEstatico(mapa, e.tile, performance.now());
  const cx = wx(e.x, e.y, TW);
  // A arte de um estático nasce ancorada no VÉRTICE DE BAIXO do losango, não
  // no centro. Dá para ver no próprio PNG: a última linha da parede tem pixel
  // só nas colunas 21-22 (um ponto), e 21 px acima ela já ocupa 0-24 — a base
  // acompanha as duas arestas de baixo e fecha no vértice. Um piso de 44x44
  // cobre exatamente o losango por essa regra, e meio tile fora por qualquer
  // outra. Ancorar no centro levantava TODO estático 22 px, e é isso que fazia
  // parede parecer flutuando sobre o piso.
  const cy = wy(e.x, e.y, TH) + TH - e.z * Z_PX;
  const sx = (cx - cam.x) * zoom + vw / 2 - t.ancoraX * zoom;
  const sy = (cy - cam.y) * zoom + vh / 2 - t.ancoraY * zoom;
  if (sx > vw + 40 || sy > vh + 40 || sx + t.w * zoom < -40 || sy + t.h * zoom < -40) return;
  const img = mapa.estaticosTs.imagem;
  if (alpha >= 1) {
    ctx.drawImage(img, t.x, t.y, t.w, t.h, sx, sy, t.w * zoom, t.h * zoom);
    return;
  }
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.drawImage(img, t.x, t.y, t.w, t.h, sx, sy, t.w * zoom, t.h * zoom);
  ctx.restore();
}

/**
 * Mapa pequeno (a arena cabe na tela inteira) desenhar cada árvore todo quadro
 * derruba o fps. O forro assa os altos uma vez; o laço só redesenha o que
 * fica na frente de um corpo.
 */
const FORRO_LADO = 4096;
const forros = new Map<string, ForroAltos | null>();

export interface ForroAltos {
  readonly canvas: HTMLCanvasElement;
  readonly origemX: number;
  readonly origemY: number;
}

export interface CorpoTela {
  readonly k: number;
  readonly sx: number;
  readonly sy: number;
}

export function forroDeAltos(mapa: MapaCarregado): ForroAltos | null {
  if (forros.has(mapa.id)) return forros.get(mapa.id) ?? null;
  const altos = mapa.altos;
  if (altos.length === 0 || altos.length > 2500) {
    forros.set(mapa.id, null);
    return null;
  }
  const TW = mapa.tileW, TH = mapa.tileH;
  const img = mapa.estaticosTs.imagem;
  if (img.width === 0) return null;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const e of altos) {
    const t = mapa.estaticosTs.tile(e.tile);
    const x0 = wx(e.x, e.y, TW) - t.ancoraX;
    const y0 = wy(e.x, e.y, TH) + TH - e.z * Z_PX - t.ancoraY;
    if (x0 < minX) minX = x0;
    if (y0 < minY) minY = y0;
    if (x0 + t.w > maxX) maxX = x0 + t.w;
    if (y0 + t.h > maxY) maxY = y0 + t.h;
  }
  const w = Math.ceil(maxX - minX) + 2;
  const h = Math.ceil(maxY - minY) + 2;
  if (!(w > 0 && h > 0) || w > FORRO_LADO || h > FORRO_LADO) {
    forros.set(mapa.id, null);
    return null;
  }
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const c = canvas.getContext('2d');
  if (!c) {
    forros.set(mapa.id, null);
    return null;
  }
  c.imageSmoothingEnabled = false;
  const ordem = altos.slice().sort((a, b) => (a.x + a.y + a.z) - (b.x + b.y + b.z));
  for (const e of ordem) {
    if (ehAnimado(mapa, e.tile)) continue;
    const t = mapa.estaticosTs.tile(e.tile);
    const sx = wx(e.x, e.y, TW) - t.ancoraX - minX;
    const sy = wy(e.x, e.y, TH) + TH - e.z * Z_PX - t.ancoraY - minY;
    c.drawImage(img, t.x, t.y, t.w, t.h, sx, sy, t.w, t.h);
  }
  const forro = { canvas, origemX: minX, origemY: minY };
  if (forros.size > 3) {
    const velho = forros.keys().next().value;
    if (velho !== undefined) forros.delete(velho);
  }
  forros.set(mapa.id, forro);
  return forro;
}

export function desenharForro(
  ctx: Ctx2D,
  forro: ForroAltos,
  cam: { x: number; y: number },
  vw: number,
  vh: number,
  zoom: number
): void {
  const left = cam.x - vw / (2 * zoom);
  const top = cam.y - vh / (2 * zoom);
  let sx = left - forro.origemX;
  let sy = top - forro.origemY;
  let sw = vw / zoom;
  let sh = vh / zoom;
  let dx = 0;
  let dy = 0;
  if (sx < 0) { dx = -sx * zoom; sw += sx; sx = 0; }
  if (sy < 0) { dy = -sy * zoom; sh += sy; sy = 0; }
  if (sx + sw > forro.canvas.width) sw = forro.canvas.width - sx;
  if (sy + sh > forro.canvas.height) sh = forro.canvas.height - sy;
  if (sw <= 0 || sh <= 0) return;
  ctx.drawImage(forro.canvas, sx, sy, sw, sh, dx, dy, sw * zoom, sh * zoom);
}

/** Alto na frente de algum corpo: precisa ser pintado de novo, por cima. */
export function estaticoNaFrente(
  mapa: MapaCarregado,
  e: MapaCarregado['altos'][number],
  cam: { x: number; y: number },
  vw: number,
  vh: number,
  zoom: number,
  corpos: readonly CorpoTela[]
): boolean {
  if (corpos.length === 0) return false;
  const t = mapa.estaticosTs.tile(e.tile);
  const TW = mapa.tileW, TH = mapa.tileH;
  const sx = (wx(e.x, e.y, TW) - cam.x) * zoom + vw / 2 - t.ancoraX * zoom;
  const sy = (wy(e.x, e.y, TH) + TH - e.z * Z_PX - cam.y) * zoom + vh / 2 - t.ancoraY * zoom;
  const sw = t.w * zoom;
  const sh = t.h * zoom;
  const k = e.x + e.y + e.z;
  const cw = 56 * zoom;
  const ch = 140 * zoom;
  for (const c of corpos) {
    if (k <= c.k) continue;
    const x0 = c.sx - cw / 2;
    const y0 = c.sy - ch;
    if (sx < x0 + cw && sx + sw > x0 && sy < y0 + ch && sy + sh > y0) return true;
  }
  return false;
}

export function dentroDaPlanta(
  e: MapaCarregado['estaticos'][number],
  x: number,
  y: number
): boolean {
  const pw = e.planta?.[0] ?? 1;
  const ph = e.planta?.[1] ?? 1;
  return x >= e.x - 1 && x <= e.x + pw && y >= e.y - 1 && y <= e.y + ph;
}

/** Projétil na passada dos altos. Ângulo já vem em radianos de tela. */
export function desenharProjetil(
  ctx: Ctx2D,
  img: HTMLImageElement,
  def: {
    readonly celula: readonly [number, number];
    readonly ancora: readonly [number, number];
    readonly quadros: number;
    readonly rotaciona: boolean;
    readonly aditivo: boolean;
  },
  quadro: number,
  mundoX: number,
  mundoY: number,
  angulo: number,
  cam: { x: number; y: number },
  vw: number,
  vh: number,
  zoom: number
): void {
  const sx = (mundoX - cam.x) * zoom + vw / 2;
  const sy = (mundoY - cam.y) * zoom + vh / 2;
  const [cw, ch] = def.celula;
  const [ax, ay] = def.ancora;
  if (sx < -80 || sy < -80 || sx > vw + 80 || sy > vh + 80) return;
  const q = ((quadro % def.quadros) + def.quadros) % def.quadros;
  ctx.save();
  ctx.imageSmoothingEnabled = false;
  if (def.aditivo) ctx.globalCompositeOperation = 'lighter';
  ctx.translate(sx, sy);
  if (def.rotaciona) ctx.rotate(angulo);
  ctx.drawImage(img, q * cw, 0, cw, ch, -ax * zoom, -ay * zoom, cw * zoom, ch * zoom);
  ctx.restore();
}

export function assarMinimapa(mapa: MapaCarregado): HTMLCanvasElement {
  const cv = document.createElement('canvas');
  cv.width = mapa.largura;
  cv.height = mapa.altura;
  const c = cv.getContext('2d');
  if (!c) throw new Error('Minimapa: sem contexto 2D.');
  for (let y = 0; y < mapa.altura; y++) {
    for (let x = 0; x < mapa.largura; x++) {
      const fam = mapa.familiaEm(x, y);
      let cor = FAMILIA_COR[fam] || '#3c5828';
      if (!mapa.andavel(x, y)) cor = fam === 'agua' ? '#1a3348' : '#2a3220';
      c.fillStyle = cor;
      c.fillRect(x, y, 1, 1);
    }
  }
  for (const e of mapa.altos) {
    if (!mapa.estaticosTs.tile(e.tile).bloqueia) continue;
    c.fillStyle = '#2a2118';
    c.fillRect(e.x, e.y, 1, 1);
  }
  return cv;
}
